import { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useGameStore } from "../../store/gameStore";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { triggerSound } from "../../lib/soundBus";
import { Swords, Dices } from "lucide-react";

const ATTACK_CLIP_LABELS = { attack: "Generic", attack_melee: "Melee", attack_magic: "Magic", attack_bow: "Bow", attack_throwing: "Throwing" };

export function parseRangeFt(range) {
  if (typeof range === "number") return range;
  if (typeof range !== "string") return null;
  const m = range.match(/(\d+)\s*feet/i);
  if (m) return parseInt(m[1], 10);
  if (/touch/i.test(range)) return 5;
  return null;
}

export function buildAttackSources(game, character, stats) {
  if (game?.system === "dnd5e") {
    return (character.dnd5e?.attacks || []).map((a, idx) => ({
      key: `dnd-${idx}`, name: a.name || "Attack", attack_bonus: parseInt(a.atk_bonus, 10) || 0,
      damage_dice: a.damage, damage_type: a.damage_type, range_ft: a.range_ft ? parseFloat(a.range_ft) : null, on_hit_effect: null,
      hit_sfx_id: a.hit_sfx_id, miss_sfx_id: a.miss_sfx_id,
    }));
  }
  const strikeSlugs = new Set((stats?.strikes || []).map((s) => s.slug));
  const strikes = (stats?.strikes || []).map((s) => ({
    key: `strike-${s.slug}`, name: s.name, attack_bonus: s.attack_bonus, damage_dice: s.damage_dice,
    damage_type: s.damage_type, range_ft: s.range_ft, on_hit_effect: null,
    hit_sfx_id: s.hit_sfx_id, miss_sfx_id: s.miss_sfx_id,
  }));
  const spells = (stats?.known_spells || []).filter((s) => s.damage && !s.save).map((s) => ({
    key: `spell-${s.slug}`, name: s.name, attack_bonus: stats?.spellcasting?.attack || 0,
    damage_dice: s.damage.dice, damage_type: s.damage.type, range_ft: parseRangeFt(s.range), on_hit_effect: null,
  }));
  const items = (character.inventory || [])
    .filter((it) => it.type === "weapon" && it.damage_dice && !strikeSlugs.has(it.slug) && it.drawn !== false)
    .map((it, idx) => ({
      key: `item-${idx}`, name: it.name, attack_bonus: stats?.ability_mods?.str || 0, damage_dice: it.damage_dice,
      damage_type: it.damage_type, range_ft: it.range_ft, on_hit_effect: it.on_hit_effect,
      hit_sfx_id: it.hit_sfx_id, miss_sfx_id: it.miss_sfx_id,
    }));
  return [...strikes, ...spells, ...items];
}

export default function AttackModal({ open, onOpenChange, gameId, game, character, stats, attackerToken, targetToken, gridSize, myMemberId, myUserName }) {
  const [rolled, setRolled] = useState({}); // key -> {outcome, in_range}
  const [chosenAttackClip, setChosenAttackClip] = useState(null);
  const sendWs = useGameStore((s) => s.sendWs);
  const grantedRangeApprovals = useGameStore((s) => s.grantedRangeApprovals);
  const freeRoam = useGameStore((s) => s.free_roam);

  if (!targetToken || !attackerToken) return null;
  const targetAc = targetToken.stat_block?.ac ?? 10;
  const pxPerFt = (gridSize || 50) / 5;
  const distFt = Math.hypot(attackerToken.x - targetToken.x, attackerToken.y - targetToken.y) / pxPerFt;
  const attackAnimKeys = Object.keys(attackerToken.animation_clips || {}).filter((k) => k === "attack" || k.startsWith("attack_"));
  const effectiveAttackClip = attackAnimKeys.includes(chosenAttackClip) ? chosenAttackClip : attackAnimKeys[0] || null;

  const sources = buildAttackSources(game, character, stats).map((s) => ({
    ...s, in_range: distFt <= (s.range_ft || 5),
  }));

  const requestId = (key) => `${attackerToken.id}-${targetToken.id}-${key}`;

  // If this NPC is already tangled up in an active Free Roam encounter and I'm helping from afar
  // but not yet in the initiative order, quietly slot me in at the bottom — no roll, just a seat
  // in line — so future turns (and the movement turn-lock) treat me as part of the fight.
  const autoJoinFreeRoamCombat = async () => {
    const encounter = (freeRoam?.encounters || []).find(
      (e) => e.active && (e.order || []).some((o) => o.token_id === targetToken.id)
    );
    if (!encounter) return;
    if ((encounter.order || []).some((o) => o.member_id === myMemberId)) return;
    try {
      await api.post(`/games/${gameId}/free-roam/encounters/${encounter.id}/join`, {
        member_id: myMemberId, character_id: character?.id || null, name: myUserName,
      });
      toast.success("You've joined the fight — added to the bottom of the initiative order");
    } catch {
      // already in it, or the encounter just ended — safe to ignore
    }
  };

  const askGm = (source) => {
    const rid = requestId(source.key);
    sendWs({ type: "range_approval_request", data: { request_id: rid, requester_member_id: myMemberId, requester_name: myUserName, target_name: targetToken.name, attack_label: source.name } });
    toast.info("Waiting for the GM to allow this out-of-range attack…");
  };

  const rollToHit = async (source) => {
    if (!character?.id) return toast.error("You need a character to attack");
    await autoJoinFreeRoamCombat();
    try {
      const { data } = await api.post(`/characters/${character.id}/attack-roll`, {
        game_id: gameId, attacker_token_id: attackerToken.id, target_token_id: targetToken.id,
        label: source.name, attack_bonus: source.attack_bonus, target_ac: targetAc, range_ft: source.range_ft,
      });
      setRolled((r) => ({ ...r, [source.key]: data }));
      const isHit = data.outcome === "hit" || data.outcome === "critical_hit";
      triggerSound(gameId, { tag: isHit ? "attack_hit" : "attack_miss", overrideAssetId: isHit ? source.hit_sfx_id : source.miss_sfx_id });
      if (attackerToken.is_animated && effectiveAttackClip) {
        api.post(`/games/${gameId}/tokens/${attackerToken.id}/play-animation`, { clip: effectiveAttackClip }).catch(() => {});
      }
    } catch {
      toast.error("Attack roll failed");
    }
  };

  const rollDamage = async (source) => {
    const outcome = rolled[source.key]?.outcome;
    try {
      await api.post(`/characters/${character.id}/damage-roll`, {
        game_id: gameId, label: source.name, dice: source.damage_dice || "1d4", bonus: 0,
        crit: outcome === "critical_hit", apply_to_token_id: targetToken.id, on_hit_effect: source.on_hit_effect,
      });
    } catch {
      toast.error("Damage roll failed");
    }
  };

  const drawWeapon = async () => {
    try {
      await api.patch(`/games/${gameId}/tokens/${attackerToken.id}`, { weapon_drawn: true });
      await api.post(`/games/${gameId}/chat`, { content: `${attackerToken.name} draws their weapon.` });
    } catch {
      toast.error("Could not draw weapon");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#322216] border-white/10 text-[#f1e0c5] max-w-md" data-testid="attack-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Swords className="w-5 h-5 text-[#D90429]" /> Attack {targetToken.name}</DialogTitle>
          <DialogDescription>AC {targetAc} · {Math.round(distFt)}ft away</DialogDescription>
        </DialogHeader>
        {attackerToken.weapon_drawn === false ? (
          <div className="space-y-3 py-2 text-center">
            <p className="text-sm text-[#f1e0c5]/70">Your weapon is stowed away — you can't strike with it yet.</p>
            <p className="text-[10px] text-[#f1e0c5]/40">{game?.system === "dnd5e" ? "Drawing it uses your free object interaction for the turn." : "Drawing it takes 1 Interact action."}</p>
            <Button data-testid="attack-draw-weapon-button" onClick={drawWeapon} className="rounded-full gap-2 bg-[#D4AF37] hover:bg-[#b8952c] text-black">Draw Weapon</Button>
          </div>
        ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
          {attackAnimKeys.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap pb-1" data-testid="attack-modal-anim-picker">
              <span className="text-[9px] uppercase text-[#f1e0c5]/30">Attack Animation:</span>
              {attackAnimKeys.map((k) => (
                <button
                  key={k}
                  data-testid={`attack-modal-anim-${k}`}
                  onClick={() => setChosenAttackClip(k)}
                  className={`text-[10px] px-2 py-0.5 rounded-full border ${effectiveAttackClip === k ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/10 text-[#f1e0c5]/50"}`}
                >
                  {ATTACK_CLIP_LABELS[k] || k}
                </button>
              ))}
            </div>
          )}
          {sources.length === 0 && <p className="text-xs text-[#f1e0c5]/40 italic">No weapons, spells, or attacks available. Equip a weapon or add an attack on your sheet first.</p>}
          {sources.map((s) => {
            const result = rolled[s.key];
            const approved = grantedRangeApprovals[requestId(s.key)];
            const canRoll = s.in_range || approved;
            return (
              <div key={s.key} className="bg-black/30 border border-white/10 rounded-xl p-2.5 space-y-1.5" data-testid={`attack-source-${s.key}`}>
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-display">{s.name}</p>
                    <p className="text-[10px] text-[#f1e0c5]/40">+{s.attack_bonus} to hit · {s.range_ft ? `${s.range_ft}ft` : "Melee (5ft)"} · {s.damage_dice} {s.damage_type}</p>
                  </div>
                  {!s.in_range && !approved && (
                    <Button data-testid={`attack-ask-gm-${s.key}`} size="sm" variant="secondary" className="rounded-full h-7 text-[10px]" onClick={() => askGm(s)}>Ask GM</Button>
                  )}
                </div>
                {!result ? (
                  <Button
                    data-testid={`attack-roll-button-${s.key}`}
                    size="sm"
                    disabled={!canRoll}
                    onClick={() => rollToHit(s)}
                    className="w-full rounded-full gap-1.5 text-xs bg-[#D90429] hover:bg-[#b8031f] disabled:opacity-40"
                  >
                    <Dices className="w-3 h-3" /> {canRoll ? (s.in_range ? "Roll to Hit" : "Roll with Disadvantage") : "Locked — needs GM approval"}
                  </Button>
                ) : (
                  <div className="space-y-1.5">
                    <p className={`text-sm font-display ${result.outcome.includes("hit") ? "text-[#4d7c0f]" : "text-[#D90429]"}`} data-testid={`attack-result-${s.key}`}>
                      Rolled {result.roll_breakdown.total} — {result.outcome.replace("_", " ")}
                    </p>
                    {(result.outcome === "hit" || result.outcome === "critical_hit") && (
                      <Button data-testid={`attack-roll-damage-${s.key}`} size="sm" variant="secondary" className="w-full rounded-full text-xs" onClick={() => rollDamage(s)}>
                        Roll Damage{result.outcome === "critical_hit" ? " (Crit!)" : ""}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
